/* eslint-disable arrow-body-style */
import * as React from "react";
import {autorun, reaction} from "mobx";
import {observer} from "mobx-react";
import {Message} from "semantic-ui-react";

import "./municipality-wizard.scss";

import EsriGraphic from "@arcgis/core/Graphic";
import EsriGraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import EsriSketchViewModel from "@arcgis/core/widgets/Sketch/SketchViewModel";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";

import {Auftragsverwaltung, PerimeterType} from "../../../../../stores/auftragsverwaltung-mobx";

import {MapState} from "../../../../../core/map-state";
import {UiState} from "../../../../../core/ui-state";

import {MapViewerConfiguration} from "../../../../../config/map-viewer-configuration";

import {Project} from "../../../../../models/project";

import {MunicipalityList} from "./municipality-list/municipality-list";
import {AddButton, ClearButton, SaveButton, UpdateButton} from "./municipality-wizard-tools/municipality-wizard-tools";
import {Municipality} from "../../../../../models/municipality";
import {AppState} from "../../../app-mobx";
import {MunicipalityStore} from "../../../../../stores/municipality-store-mobx";
import {PerimeterTypeSelector} from "../perimeter-type-selector/perimeter-type-selector";
import EsriGeometry from "@arcgis/core/geometry/Geometry";

export enum MunicipalityWizardMode {
    MunicipalitySelector = "MunicipalitySelector",
    PerimeterSelector = "perimeterSelector"
}

export interface MunicipalityWizardProps {
    auftragsverwaltung: Auftragsverwaltung;
    mapViewerConfig: MapViewerConfiguration;
    project: Project | null;
    mapStore: MapState;
    appStore: AppState;
    uiStore: UiState;
}

/**
 * Wizard to create polygon features based on Municipalities
 */
@observer
export class MunicipalityWizard extends React.Component<MunicipalityWizardProps, any> {
    sketchWatchHandle: any;
    autorunDisposers: any[] = [];

    municipalityStore: MunicipalityStore;
    sketchLayer: EsriGraphicsLayer;
    proposalLayer: EsriGraphicsLayer;
    highlightLayer: EsriGraphicsLayer;
    sketchViewModel: EsriSketchViewModel;

    constructor(props: MunicipalityWizardProps) {
        super(props);
        this.state = {
            mode: null,  // MunicipalityWizardMode
            updatePerimeter: null,  // the perimeter selected to update
            perimeterProposal: null,  // the proposal generated from all selected Municipalities
            noMunicipalitiesFoundError: false,
            selectedPerimeterType: null  // selected perimeter type
        };
        this.sketchLayer = new EsriGraphicsLayer(
            {
                listMode: "hide",
                title: "SketchLayer MunicipalityWizard",
                opacity: 0.7
            }
        );
        this.proposalLayer = new EsriGraphicsLayer(
            {
                listMode: "hide",
                title: "ProposalLayer MunicipalityWizard",
                opacity: 0.7
            }
        );
        this.highlightLayer = new EsriGraphicsLayer(
            {
                listMode: "hide",
                title: "HighlightLayer MunicipalityWizard"
            }
        );

        this.initStore();
        this.handlePerimeterTypeSelection = this.handlePerimeterTypeSelection.bind(this);
    }

    componentDidMount(): void {
        // rerun until map view is ready and layer list view model is loaded
        this.autorunDisposers.push(autorun((r: any) => {
            const {auftragsverwaltung, mapStore} = this.props;

            if (!auftragsverwaltung.selectedProject || !auftragsverwaltung.apiLoaded) {
                return;
            }

            if (!mapStore.map || !mapStore.view) {
                return;
            }
            r.dispose();

            // update the graphic on map showing the unioned geometry of all selected municipalities
            // whenever a municipality is added to or removed from the list
            this.autorunDisposers.push(reaction(
                () => [...this.municipalityStore?.items.values()],
                (municipalities) => {
                    this.updateMunicipalityPerimeterProposal(municipalities);
                }
            ));

            // add proposal layer to the map
            if (this.proposalLayer && !mapStore?.map.layers.includes(this.proposalLayer)) {
                mapStore?.map.add(this.proposalLayer);
            }
            // add sketch layer to the map
            if (this.sketchLayer && !mapStore?.map.layers.includes(this.sketchLayer)) {
                mapStore?.map.add(this.sketchLayer);
            }
            // add highlight layer
            if (this.highlightLayer && !mapStore?.map.layers.includes(this.highlightLayer)) {
                mapStore?.map.add(this.highlightLayer);
            }

            this.clearHighlights();

            this.initSketchViewModel();

            // TODO: workaround. Direktes aufrufen von wirft das erste mal einen Fehler in sketchViewModel.create
            setTimeout(() => {
                this.activateMunicipalitySelector();
            }, 200);
        }));
    }

    componentDidUpdate() {}


    componentWillUnmount(): void {
        this.clearHighlights();
        this.deactivateMunicipalitySelector();

        this.autorunDisposers.forEach((dispose: any) => {
            dispose();
        });
        this.autorunDisposers.length = 0;

        this.sketchViewModel.destroy();
    }

    render() {
        const {auftragsverwaltung, project} = this.props;
        const {mode, noMunicipalitiesFoundError} = this.state;
        const hasMunicipalitiesSelected = this.municipalityStore?.items.size > 0;

        return (
            <div className="municipality-wizard">
                <PerimeterTypeSelector project={project} onSelect={this.handlePerimeterTypeSelection} />
                {!hasMunicipalitiesSelected &&
                    <div className="municipality-wizard-perimeter-selector">
                        <Message
                            attached info size={"mini"}
                            header="A) Bestehenden Perimeter auswählen"
                            list={[
                                "In Karte auf Perimeter klicken"
                            ]}
                        />
                        <div className="municipality-wizard-tools">
                            <UpdateButton
                                active={mode === MunicipalityWizardMode.PerimeterSelector}
                                onUpdate={() => {
                                    this.activatePerimeterSelector();
                                }}
                            />
                        </div>
                    </div>
                }
                <div className="municipality-wizard-municipality-selector">
                    <Message
                        attached info size={"mini"}
                        header="B) Gemeinden auswählen"
                        list={[
                            `Mit + Gemeinden aus Karte ${hasMunicipalitiesSelected ? "hinzufügen" : "auswählen"} (Rechteck aufziehen)`,
                            "Gemeinden in Liste prüfen / entfernen"
                        ]}
                    />
                    <div className="municipality-wizard-tools">
                        <AddButton
                            active={mode === MunicipalityWizardMode.MunicipalitySelector}
                            onAdd={() => {
                                this.activateMunicipalitySelector();
                            }}
                        />
                        {hasMunicipalitiesSelected &&
                            <>
                                <ClearButton onClear={() => {
                                    this.clearSelectedMunicipalities();
                                }} />
                                <SaveButton onSave={() => {
                                    this.saveProjectPerimeter();
                                }} />
                            </>
                        }
                    </div>
                    <div className="municipality-list">
                        <MunicipalityList
                            auftragsverwaltung={auftragsverwaltung}
                            municipalityStore={this.municipalityStore}
                            onHover={(e, municipality) => {
                                this.highlightMunicipalityFeature(municipality?.feature);
                            }}
                        />
                        {noMunicipalitiesFoundError &&
                            <Message
                                warning size={"mini"}
                                header="Nicht zusammengesetzt aus Gemeinden"
                                content="Das gewählte Feature besteht nicht aus Gemeinden.
                                 Es kann mit diesem Werkzeug nicht bearbeitet werden."
                            />
                        }
                    </div>
                </div>
            </div>
        );
    }

    private handlePerimeterTypeSelection(type: PerimeterType): void {
        this.setState({selectedPerimeterType: type});
        // update proposal when perimeter type is changed
        this.updateMunicipalityPerimeterProposal([...this.municipalityStore?.items.values()], type);
    }

    private updateMunicipalityPerimeterProposal(municipalities: Municipality[], type?: PerimeterType): void {
        const {project, auftragsverwaltung, uiStore, appStore} = this.props;
        const {updatePerimeter, selectedPerimeterType} = this.state;

        uiStore?.setPopupVisible(false);
        this.clearProposal(); // clear previously added proposals

        if (municipalities.length === 0 || !project?.id) {
            return;
        }

        // merge the geometries of the municipalities and add it as sketch proposal
        const geometries = municipalities.map((p) => p.feature?.geometry);
        const selectedMunicipalityGeometry = geometryEngine.union(geometries);

        const store = auftragsverwaltung.ProjectPerimeterStore;
        if (!store) {
            throw new Error("Perimeter store undefined when creating feature");
        }

        if (updatePerimeter) {
            // user selected an existing perimeter
            // use the new geometry to update the selected project perimeter
            updatePerimeter.feature.geometry = selectedMunicipalityGeometry;
            this.setState({
                perimeterProposal: updatePerimeter
            });
            this.showProposalOnMap(selectedMunicipalityGeometry);
            // remember in store
            store.proposal = updatePerimeter;
        }
        else {
            // create perimeter from template with the new geometry
            const user = appStore.esriCredential?.userId || "";
            store.newItem(project.id, user, selectedMunicipalityGeometry)
                .then((perimeter) => {
                    if (perimeter) {
                        // set type base on project type
                        // perimeter type selector returns type based on project type and selection
                        perimeter.objectType = type || selectedPerimeterType;

                        this.setState({
                            perimeterProposal: perimeter
                        });
                        this.showProposalOnMap(selectedMunicipalityGeometry);
                        // remember in store
                        store.proposal = perimeter;
                    }
                });
        }
    }

    /**
     * Show graphic on map
     * @param geometry
     * @private
     */
    private showProposalOnMap(geometry: __esri.Geometry): void {
        const {mapViewerConfig} = this.props;

        if (!geometry) {
            throw new Error("Error placing proposal on map. Geometry undefined.");
        }

        const symbol = mapViewerConfig.symbols["perimeterProposal"] as __esri.Symbol;
        const tempGraph = new EsriGraphic({
            geometry,
            symbol,
            layer: this.proposalLayer
        });
        this.proposalLayer.graphics.add(tempGraph);
    }

    /**
     * Select municipalities from municipality layer with specified geometry
     * @param geometry
     * @param spatialRelationship
     * @private
     */
    private selectMunicipalities(
        geometry: __esri.Geometry,
        spatialRelationship?: "intersects"
        | "contains"
        | "crosses"
        | "disjoint"
        | "envelope-intersects"
        | "index-intersects"
        | "overlaps"
        | "touches"
        | "within"
        | "relation") {

        this.municipalityStore?.queryFeatures(undefined, geometry, spatialRelationship)
            .then((municipalities) => {
                if (municipalities && municipalities.length > 0) {
                    // remember selected municipalities
                    this.municipalityStore?.addMunicipalities(municipalities);
                    // geometry in map and editor is updated by autorun when municipalities in municipality store are updated
                }
                else {
                    // geometry is not composed of municipalities
                    this.setState({
                        noMunicipalitiesFoundError: true
                    });
                }
                // clear drawn polygon from map
                this.clearSelectionSketch();
            })
            .catch((error) => {
                console.error(error);
            });
    }

    /**
     * Select perimeter by point
     * @param geometry
     * @private
     */
    private selectPerimeter(geometry: __esri.Geometry): void {
        const {auftragsverwaltung} = this.props;

        auftragsverwaltung.ProjectPerimeterStore?.queryFeatures(
            undefined,
            undefined,
            geometry)
            .then((perimeters) => {
                if (perimeters && perimeters.length > 0) {
                    const perimeter = perimeters[0];
                    this.setState({
                        updatePerimeter: perimeter
                    });
                    const  bufferedGeometry = geometryEngine.buffer(perimeter.feature?.geometry, -10, "meters");
                    this.selectMunicipalities(bufferedGeometry as EsriGeometry, "intersects");
                }
                // clear drawn polygon from map
                this.clearSelectionSketch();
            })
            .catch((error) => {
                console.error(error);
            });
    }

    /**
     * Save the proposal as perimeter
     * @private
     */
    private saveProjectPerimeter(): void {
        const {auftragsverwaltung, appStore} = this.props;
        const {updatePerimeter, perimeterProposal} = this.state;

        if (!perimeterProposal) {
            return;
        }

        if (updatePerimeter) {
            // ask if existing perimeter should be updated
            if (!confirm("Bestehenden Perimeter ändern?")) {
                return;
            }
        }

        const user = appStore.esriCredential?.userId || "";
        auftragsverwaltung.ProjectPerimeterStore?.save(perimeterProposal, undefined, user).then(() => {
            this.clearSelectedMunicipalities();
            this.setState({
                updatePerimeter: null,
                perimeterProposal: null
            });
            auftragsverwaltung.ProjectPerimeterStore?.clearProposal();
        });
    }

    /**
     * Clear selection layers
     * @private
     */
    private clearSelectionSketch(): void {
        this.sketchLayer?.removeAll();
    }
    /**
     * Clear proposal layers
     * @private
     */
    private clearProposal(): void {
        const {auftragsverwaltung} = this.props;
        this.proposalLayer?.removeAll();
        auftragsverwaltung.ProjectPerimeterStore?.clearProposal();
    }
    /**
     * Clear highlight layers
     * @private
     */
    private clearHighlights(): void {
        this.highlightLayer?.removeAll();
    }

    private clearSelectedMunicipalities(): void {
        this.municipalityStore?.clearItems();
        this.clearHighlights();
        this.clearProposal();
    }

    /**
     * Highlight municipality features in the map
     * @param feature
     * @private
     */
    private highlightMunicipalityFeature(feature: __esri.Graphic): void {
        const {mapViewerConfig} = this.props;

        this.clearHighlights();

        const symbolConfig = mapViewerConfig.symbols["municipalityHighlightSymbol"];
        const graphic = new EsriGraphic({geometry: feature.geometry, symbol: symbolConfig});
        this.highlightLayer.add(graphic);
    }

    /**
     * Activate tool to select municipalities by dragging rectangle to the map
     * @private
     */
    private activateMunicipalitySelector(): void {
        if (!this.sketchViewModel) {
            throw new Error("MunicipalityWizard: SketchViewModel not initialized.");
        }
        this.sketchWatchHandle?.remove();

        this.setState({
            mode: MunicipalityWizardMode.MunicipalitySelector,
            updatePerimeter: null
        });

        this.sketchWatchHandle =
            this.sketchViewModel.on("create", async (event) => {
                if (event.state === "complete") {
                    this.selectMunicipalities(event.graphic?.geometry);
                }
            });


        // activate tool to draw rectangle
        this.sketchViewModel.create("rectangle");
    }

    /**
     * Activate tool to select existing perimeter and parse as municipality list
     * @private
     */
    private activatePerimeterSelector(): void {
        if (!this.sketchViewModel) {
            throw new Error("MunicipalityWizard: SketchViewModel not initialized.");
        }
        this.sketchWatchHandle?.remove();

        this.setState({
            mode: MunicipalityWizardMode.PerimeterSelector,
            noMunicipalitiesFoundError: false
        });

        this.sketchWatchHandle =
            this.sketchViewModel.on("create", async (event) => {
                if (event.state === "complete") {
                    this.selectPerimeter(event.graphic?.geometry);
                }
            });


        // activate tool to draw point
        this.sketchViewModel.create("point");
    }

    private deactivateMunicipalitySelector(): void {
        this.setState({
            updatePerimeter: null,
            noMunicipalitiesFoundError: false
        });
        this.clearSelectionSketch();
        this.sketchViewModel.cancel();
        this.sketchWatchHandle?.remove();
        this.clearProposal();
    }

    private initSketchViewModel(): void {
        const {auftragsverwaltung, mapStore, mapViewerConfig} = this.props;

        // initialize model to draw a selection box
        this.sketchViewModel = new EsriSketchViewModel({
            view: mapStore?.view,
            layer: this.sketchLayer,
            tooltipOptions: {
                enabled: true
            },
            polygonSymbol: mapViewerConfig.symbols["municipalitySelection"] as __esri.SimpleFillSymbol
        });
        // watch changes in application edit mode
        this.autorunDisposers.push(autorun((editingReaction: any) => {
            if(!auftragsverwaltung.isEditingProject) {
                editingReaction.dispose();
                // deactivate municipality selector when editing mode is disabled
                this.deactivateMunicipalitySelector();
            }
        }));
    }

    private initStore(): void {
        const {mapViewerConfig} = this.props;
        const layerConfig = mapViewerConfig.auftragsverwaltungApi.getLayerConfig("municipality");
        if (!layerConfig) {
            throw new Error("Configuration not found for municipality layer.");
        }
        this.municipalityStore = new MunicipalityStore({
            layerConfig
        });
        this.municipalityStore.loadApi();
    }
}
